import { createServerFn } from '@tanstack/react-start';
import { getCookie } from '@tanstack/react-start/server';
import { AppwriteException, Query, type Models } from 'node-appwrite';
import { z } from 'zod';

import {
    STAFF_ROLE_AUTHOR,
    STAFF_ROLE_OWNER,
    staffRoleFromMembershipRoles,
    type StaffRole,
} from '@/lib/admins';
import { absoluteUrl } from '@/lib/site-url';
import { requireAdminAccess } from '@/server/functions/auth';
import { createSessionClient } from '@/server/lib/appwrite';
import { getAdminsTeamId } from '@/server/lib/admins';
import { SESSION_COOKIE } from '@/server/lib/session';

export type AdminMemberRow = {
    membershipId: string;
    userId: string;
    name: string;
    email: string;
    roles: string[];
    staffRole: StaffRole;
    invited: string;
    joined: string;
    confirm: boolean;
};

function throwAppwriteError(error: unknown): never {
    const appwriteError = error as AppwriteException;
    throw {
        message: appwriteError.message || 'Something went wrong',
        status: appwriteError.code || 500,
    };
}

function mapMembership(row: Models.Membership): AdminMemberRow {
    return {
        membershipId: row.$id,
        userId: row.userId,
        name: row.userName || row.userEmail || row.userId,
        email: row.userEmail || '',
        roles: row.roles,
        staffRole: staffRoleFromMembershipRoles(row.roles),
        invited: row.invited,
        joined: row.joined,
        confirm: row.confirm,
    };
}

async function operatorTeams() {
    const sessionSecret = getCookie(SESSION_COOKIE);
    if (!sessionSecret) {
        throw {
            message: 'נדרשת התחברות כדי לנהל את הצוות.',
            status: 401,
        };
    }
    const client = await createSessionClient(sessionSecret, {
        asOperator: true,
    });
    return client.teams;
}

async function listTeamMemberships(teamId: string) {
    const teams = await operatorTeams();
    const memberships: Models.Membership[] = [];
    let total = 0;
    let cursor: string | undefined;

    for (;;) {
        const queries = [Query.limit(100), Query.orderDesc('$createdAt')];
        if (cursor) queries.push(Query.cursorAfter(cursor));
        const result = await teams.listMemberships({
            teamId,
            queries,
        });
        total = result.total;
        memberships.push(...result.memberships);
        if (result.memberships.length < 100) break;
        const last = result.memberships.at(-1);
        if (!last) break;
        cursor = last.$id;
    }

    return {
        total,
        rows: memberships.map(mapMembership),
    };
}

function confirmedOwnerCount(rows: AdminMemberRow[]) {
    return rows.filter((row) => row.confirm && row.staffRole === STAFF_ROLE_OWNER)
        .length;
}

export const adminListAdminsFn = createServerFn({ method: 'GET' }).handler(
    async () => {
        await requireAdminAccess();
        const teamId = getAdminsTeamId();
        try {
            const teams = await operatorTeams();
            const [team, members] = await Promise.all([
                teams.get({ teamId }),
                listTeamMemberships(teamId),
            ]);
            return {
                teamId: team.$id,
                teamName: team.name,
                total: members.total,
                rows: members.rows,
            };
        } catch (error) {
            if (
                error &&
                typeof error === 'object' &&
                'message' in error &&
                'status' in error
            ) {
                throw error;
            }
            throwAppwriteError(error);
        }
    },
);

const staffRoleSchema = z.enum([STAFF_ROLE_OWNER, STAFF_ROLE_AUTHOR]);

/**
 * Credentials for a browser `Teams.createMembership` call.
 * Server-side createMembership (node-appwrite) does not send invite mail.
 */
export const adminStaffInviteContextFn = createServerFn({ method: 'POST' }).handler(
    async () => {
        await requireAdminAccess();
        const sessionSecret = getCookie(SESSION_COOKIE);
        if (!sessionSecret) {
            throw {
                message: 'נדרשת התחברות כדי להזמין לצוות.',
                status: 401,
            };
        }

        try {
            const { client } = await createSessionClient(sessionSecret, {
                asOperator: true,
            });
            return {
                session: sessionSecret,
                endpoint: client.config.endpoint.replace(/\/+$/, ''),
                projectId: client.config.project,
                teamId: getAdminsTeamId(),
                inviteUrl: absoluteUrl('/invite'),
            };
        } catch (error) {
            if (
                error &&
                typeof error === 'object' &&
                'message' in error &&
                'status' in error
            ) {
                throw error;
            }
            throwAppwriteError(error);
        }
    },
);

const updateAdminRoleSchema = z.object({
    membershipId: z.string().min(1),
    role: staffRoleSchema,
});

export const adminUpdateAdminRoleFn = createServerFn({ method: 'POST' })
    .validator(updateAdminRoleSchema)
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const teamId = getAdminsTeamId();

        try {
            const teams = await operatorTeams();
            const { rows } = await listTeamMemberships(teamId);
            const current = rows.find((row) => row.membershipId === data.membershipId);
            if (!current) {
                throw { message: 'החברות בצוות לא נמצאה.', status: 404 };
            }

            if (
                current.staffRole === STAFF_ROLE_OWNER &&
                data.role === STAFF_ROLE_AUTHOR &&
                confirmedOwnerCount(rows) <= 1
            ) {
                throw {
                    message: 'לא ניתן להוריד את המנהל האחרון לתפקיד כותב.',
                    status: 400,
                };
            }

            const membership = await teams.updateMembership({
                teamId,
                membershipId: data.membershipId,
                roles: [data.role],
            });

            return mapMembership(membership);
        } catch (error) {
            if (
                error &&
                typeof error === 'object' &&
                'message' in error &&
                'status' in error
            ) {
                throw error;
            }
            throwAppwriteError(error);
        }
    });

const removeAdminSchema = z.object({
    membershipId: z.string().min(1),
});

export const adminRemoveAdminFn = createServerFn({ method: 'POST' })
    .validator(removeAdminSchema)
    .handler(async ({ data }) => {
        const auth = await requireAdminAccess();
        const teamId = getAdminsTeamId();

        try {
            const teams = await operatorTeams();
            const membership = await teams.getMembership({
                teamId,
                membershipId: data.membershipId,
            });
            const current = mapMembership(membership);

            if (current.userId === auth.currentUser?.$id) {
                throw {
                    message: 'לא ניתן להסיר את עצמכם מצוות הניהול.',
                    status: 400,
                };
            }

            if (current.staffRole === STAFF_ROLE_OWNER) {
                const { rows } = await listTeamMemberships(teamId);
                if (confirmedOwnerCount(rows) <= 1) {
                    throw {
                        message: 'לא ניתן להסיר את המנהל האחרון.',
                        status: 400,
                    };
                }
            }

            await teams.deleteMembership({
                teamId,
                membershipId: data.membershipId,
            });

            return { ok: true as const };
        } catch (error) {
            if (
                error &&
                typeof error === 'object' &&
                'message' in error &&
                'status' in error
            ) {
                throw error;
            }
            throwAppwriteError(error);
        }
    });
